/**
 * Client-side activity log for PPPC policies.
 *
 * The smplify backend doesn't (yet) expose per-policy audit history, so we
 * persist a lightweight event stream in localStorage keyed by policy ID.
 * Each entry records who performed an action (best-effort, from the JWT)
 * and when, plus optional pointers to the *app* and *device* the event
 * relates to so the same store can power three filtered views:
 *
 *   - by policy (PPPC detail page)
 *   - by app    (Application detail page — bundleId or appId)
 *   - by device (Device detail page — deviceId)
 *
 * Entries are append-only and capped at MAX_ENTRIES_PER_POLICY per policy
 * to keep storage bounded. When a real backend audit history exists, swap
 * the read paths for the API and keep this as a fallback for offline demos.
 */

const STORAGE_KEY = "pppc-activity-log:v1";
const MAX_ENTRIES_PER_POLICY = 100;

export type PppcActivityAction =
  | "created"
  | "updated"
  | "deleted"
  /** Tag set bound to the policy changed (deployment scope). */
  | "tagged"
  /** Wizard published the policy with intent to deploy to a device set. */
  | "deployed"
  /** Backend reports the profile is installed on a specific device. */
  | "installed";

/**
 * Snapshot of a PPPC policy at the moment of the recorded action.
 * Used by the "Compare with previous" view to diff successive updates
 * without a backend audit history.
 */
export interface PppcActivitySnapshot {
  appName: string;
  identifier: string;
  identifierType: "bundleID" | "path";
  codeRequirement: string;
  /** Service key → "allow" | "deny" (omitted services count as "unset"). */
  choices: Record<string, "allow" | "deny">;
}

export interface PppcActivityEntry {
  id: string;
  policyId: string;
  action: PppcActivityAction;
  /** Email or display name of the user, when known. */
  actor: string | null;
  /** ISO timestamp. */
  at: string;
  /** Optional human summary. */
  summary?: string;
  /** Optional structured detail. */
  details?: Record<string, unknown>;
  /** Policy state captured at the time of this entry (for diffs). */
  snapshot?: PppcActivitySnapshot;
  /**
   * Cross-entity pointers so the same store can be filtered per-app and
   * per-device. All optional — older entries (created/updated) won't
   * have device pointers.
   */
  appId?: string;
  bundleId?: string;
  deviceId?: string;
  /** Tag IDs bound to the policy after this event (for tagged/deployed). */
  tagIds?: string[];
  /** Tag IDs bound before this event (for tagged). */
  previousTagIds?: string[];
}

type Store = Record<string, PppcActivityEntry[]>;

function readStore(): Store {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

function writeStore(store: Store) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota exceeded or storage disabled — drop silently.
  }
}

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function notify(policyId: string, scope?: { deviceId?: string; appId?: string; bundleId?: string }) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("pppc-activity-updated", {
      detail: { policyId, ...scope },
    }),
  );
}

export function recordPppcActivity(
  entry: Omit<PppcActivityEntry, "id" | "at"> & { at?: string },
): PppcActivityEntry {
  const full: PppcActivityEntry = {
    id: makeId(),
    at: entry.at ?? new Date().toISOString(),
    ...entry,
  };
  const store = readStore();
  const existing = store[full.policyId] ?? [];
  const next = [full, ...existing].slice(0, MAX_ENTRIES_PER_POLICY);
  store[full.policyId] = next;
  writeStore(store);
  notify(full.policyId, {
    appId: full.appId,
    bundleId: full.bundleId,
    deviceId: full.deviceId,
  });
  return full;
}

/**
 * Like recordPppcActivity, but no-ops if an equivalent entry already exists
 * for the same (policyId, deviceId, action) tuple. Used by the device-side
 * "installed" emitter so reloading the device page doesn't keep appending
 * the same install confirmation.
 */
export function recordPppcActivityIfNew(
  entry: Omit<PppcActivityEntry, "id" | "at"> & { at?: string },
): PppcActivityEntry | null {
  const store = readStore();
  const existing = store[entry.policyId] ?? [];
  const dup = existing.find(
    (e) =>
      e.action === entry.action &&
      (e.deviceId ?? null) === (entry.deviceId ?? null) &&
      (entry.action !== "installed" || (e.details?.status ?? null) === (entry.details?.status ?? null)),
  );
  if (dup) return null;
  return recordPppcActivity(entry);
}

export function getPppcActivity(policyId: string): PppcActivityEntry[] {
  return readStore()[policyId] ?? [];
}

/**
 * All PPPC events tied to an app — matched by appId or bundleId. Returned
 * newest-first across all policies (an app can have multiple historical
 * policies if its bundleId changed).
 */
export function getPppcActivityForApp(args: {
  appId?: string;
  bundleId?: string;
}): PppcActivityEntry[] {
  const { appId, bundleId } = args;
  if (!appId && !bundleId) return [];
  const store = readStore();
  const out: PppcActivityEntry[] = [];
  for (const entries of Object.values(store)) {
    for (const e of entries) {
      if (appId && e.appId === appId) out.push(e);
      else if (bundleId && e.bundleId === bundleId) out.push(e);
    }
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

/** All PPPC events tied to a device, newest-first. */
export function getPppcActivityForDevice(deviceId: string): PppcActivityEntry[] {
  if (!deviceId) return [];
  const store = readStore();
  const out: PppcActivityEntry[] = [];
  for (const entries of Object.values(store)) {
    for (const e of entries) {
      if (e.deviceId === deviceId) out.push(e);
    }
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

/**
 * All PPPC events across every policy in the store, newest-first.
 * Powers the global Logs page; consumers apply their own filters.
 */
export function getAllPppcActivity(): PppcActivityEntry[] {
  const store = readStore();
  const out: PppcActivityEntry[] = [];
  for (const entries of Object.values(store)) {
    for (const e of entries) out.push(e);
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

export function clearPppcActivity(policyId: string) {
  const store = readStore();
  if (policyId in store) {
    delete store[policyId];
    writeStore(store);
  }
}
